import { useSelector, useDispatch } from "react-redux";
import { Typography, useTheme, FormControlLabel } from "@mui/material";
import AppState from "../../store/AppState";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import CustomDialog from "../common/DialogContainer";
import { useIntl } from "react-intl";
import ButtonComponent from "../common/ButtonComponent";
import { StyledDiv, styleClasses, useStyles } from "./LinkedCategoriesStyles";
import { StyledtextSelect } from "components/remove-products-modal/RemoveProductsModalStyles";
import { StyledtextUnSelect } from "components/remove-products-modal/RemoveProductsModalStyles";
import CheckboxComponent from "../common/Checkbox";
import { useState, useRef } from "react";
import { selectLinkedCategorySetByCategory } from "../../store/linked-categories/LinkedCategoriesSelectors";
import React from "react";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import {
  selectCurrentStoreId,
  selectCurrentLocale,
} from "store/store-list/StoreListSelectors";
import { submitSyncCategoriesRequest } from "store/sync-categories/SyncCategoriesActions";
import { getUserEmail } from "services/UserService";

export const LinkedCategoriesModalId = "LinkedCategoriesModal";

const LinkedCategoriesModal = (props) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const intl = useIntl();
  const storeId = useSelector(selectCurrentStoreId);
  const catalogId = useSelector(selectCurrentCatalogId);
  const categoryId = useSelector(selectCurrentCategory);
  const localeCode = useSelector(selectCurrentLocale);

  const linkedCategorySetByCategory = useSelector((state: AppState) =>
    selectLinkedCategorySetByCategory(state, storeId, catalogId, categoryId),
  );

  const [checkboxStates, setCheckboxStates] = useState({
    addProducts: false,
    removeProducts: false,
  });

  const [linkedCategoryCheckboxes, setLinkedCategoryCheckboxes] = useState<
    Record<string, boolean>
  >({});

  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, LinkedCategoriesModalId),
  );

  const initializedRef = useRef(false);

  // Initialize linked category checkboxes as selected by default
  React.useEffect(() => {
    if (
      isModalOpen &&
      linkedCategorySetByCategory.length > 0 &&
      !initializedRef.current
    ) {
      const defaultSelected = linkedCategorySetByCategory.reduce(
        (acc, category) => {
          const key = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setLinkedCategoryCheckboxes(defaultSelected);
      initializedRef.current = true;
    }
  }, [isModalOpen, linkedCategorySetByCategory]);

  const handleClose = () => {
    setCheckboxStates({
      addProducts: false,
      removeProducts: false,
    });
    const defaultSelected = linkedCategorySetByCategory.reduce(
      (acc, category) => {
        const key = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    initializedRef.current = false;
    setLinkedCategoryCheckboxes(defaultSelected);
    dispatch(setModalState(LinkedCategoriesModalId, false));
  };

  const handleCheckboxChange =
    (option: "addProducts" | "removeProducts") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckboxStates((prev) => ({
        ...prev,
        [option]: event.target.checked,
      }));
    };

  const handleLinkedCategoryCheckboxChange =
    (categoryKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setLinkedCategoryCheckboxes((prev) => ({
        ...prev,
        [categoryKey]: event.target.checked,
      }));
    };

  const handleSelectAll = () => {
    setCheckboxStates({
      addProducts: true,
      removeProducts: true,
    });
    // Also select all linked category checkboxes
    if (linkedCategorySetByCategory) {
      const allSelected = linkedCategorySetByCategory.reduce(
        (acc, category) => {
          const key = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setLinkedCategoryCheckboxes(allSelected);
    }
  };

  const handleUnselectAll = () => {
    setCheckboxStates({
      addProducts: false,
      removeProducts: false,
    });
    // Also unselect all linked category checkboxes
    if (linkedCategorySetByCategory) {
      const allUnselected = linkedCategorySetByCategory.reduce(
        (acc, category) => {
          const key = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setLinkedCategoryCheckboxes(allUnselected);
    }
  };

  const handleSync = () => {
    // Filter to only include selected categories
    const selectedCategories = linkedCategorySetByCategory.filter(
      (category) => {
        const categoryKey = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
        return linkedCategoryCheckboxes[categoryKey];
      },
    );

    const payload = {
      source: {
        storeId,
        catalogId,
        categoryId,
        localeCode,
      },
      targets: selectedCategories.map((category) => ({
        storeId: category.storeId,
        catalogId: category.catalogId,
        categoryId: category.categoryId,
      })),
      options: {
        addProducts: checkboxStates.addProducts,
        removeProducts: checkboxStates.removeProducts,
      },
      triggeredBy: getUserEmail(),
    };
    dispatch(submitSyncCategoriesRequest(payload));
    handleClose();
  };

  // Check if any checkboxes are selected
  const hasSelectedCategories = Object.values(linkedCategoryCheckboxes).some(
    (checked) => checked,
  );
  const isSyncDisabled = !hasSelectedCategories;

  const linkedCategoriesModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "linkedCategoriesModal.header",
          defaultMessage: "Linked Categories",
        })}
      </Typography>
    );
  };

  const linkedCategoriesModalContent = () => {
    return (
      <>
        <StyledDiv style={classes.optionsContainer}>
          <Typography variant="subHeader" style={classes.instructionText}>
            {intl.formatMessage({
              id: "linkedCategoriesModal.instruction",
              defaultMessage: "Select options that may apply:",
            })}
          </Typography>
          <StyledDiv style={classes.options}>
            <StyledtextSelect
              className={styleClasses.spanSelect}
              onClick={handleSelectAll}
            >
              {intl.formatMessage({
                id: "general.selectAllLabel",
                defaultMessage: "Select All",
              })}
            </StyledtextSelect>
            /
            <StyledtextUnSelect
              className={styleClasses.spanUnselect}
              onClick={handleUnselectAll}
            >
              {intl.formatMessage({
                id: "general.unSelectAllLabel",
                defaultMessage: "Unselect All",
              })}
            </StyledtextUnSelect>
          </StyledDiv>
        </StyledDiv>
        <StyledDiv style={classes.checkboxContainer}>
          <FormControlLabel
            control={
              <CheckboxComponent
                checkboxId="LinkedCategoriesModal"
                checked={checkboxStates.addProducts}
                onChange={handleCheckboxChange("addProducts")}
              />
            }
            label={intl.formatMessage({
              id: "linkedCategoriesModal.addProducts",
              defaultMessage:
                "Add products to a target category if they exist in the source but not in the target",
            })}
            style={classes.checkboxOption}
          />
          <FormControlLabel
            control={
              <CheckboxComponent
                checkboxId="LinkedCategoriesModal"
                checked={checkboxStates.removeProducts}
                onChange={handleCheckboxChange("removeProducts")}
              />
            }
            label={intl.formatMessage({
              id: "linkedCategoriesModal.removeProducts",
              defaultMessage:
                "Remove products from a target category if they exist in the target but not in the source",
            })}
            style={classes.checkboxOption}
          />
        </StyledDiv>
        <StyledDiv style={classes.linkedCategoriesSection}>
          <Typography variant="subHeader" style={classes.sectionTitle}>
            {intl.formatMessage({
              id: "linkedCategoriesModal.sectionTitle",
              defaultMessage:
                "The following checked linked category will be updated:",
            })}
          </Typography>
          <StyledDiv style={classes.linkedCategoriesList}>
            {linkedCategorySetByCategory.map((category, index) => {
              const categoryKey = `${category.storeId}/${category.catalogId}/${category.categoryId}`;
              return (
                <FormControlLabel
                  key={index}
                  control={
                    <CheckboxComponent
                      checkboxId="LinkedCategoriesModal"
                      checked={linkedCategoryCheckboxes[categoryKey] || false}
                      onChange={handleLinkedCategoryCheckboxChange(categoryKey)}
                    />
                  }
                  label={categoryKey}
                  style={classes.linkedCategoryItem}
                />
              );
            })}
          </StyledDiv>
        </StyledDiv>
      </>
    );
  };

  const dialogActions = () => {
    return (
      <>
        <div style={classes.dailogAction}>
          <ButtonComponent
            color="secondaryButtonColorCTABlue"
            variant="outlined"
            onClick={handleClose}
          >
            {intl.formatMessage({
              id: "linkedCategoriesModal.cancel",
              defaultMessage: "Cancel",
            })}
          </ButtonComponent>
          <ButtonComponent
            color="primary"
            variant="contained"
            justifyContent="center"
            onClick={handleSync}
            disabled={isSyncDisabled}
          >
            {intl.formatMessage({
              id: "linkedCategoriesModal.sync",
              defaultMessage: "Sync",
            })}
          </ButtonComponent>
        </div>
      </>
    );
  };

  return (
    <>
      <CustomDialog
        open={isModalOpen}
        onClose={() => handleClose()}
        title={linkedCategoriesModalTitle()}
        actions={dialogActions()}
        fullWidth
        maxWidth={"md"}
      >
        <>{linkedCategoriesModalContent()}</>
      </CustomDialog>
    </>
  );
};

export default LinkedCategoriesModal;
